import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import LanguageSwitcher from '../LanguageSwitcher';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t, lang } = useLanguage();

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const prefix = useMemo(() => (lang === 'ar' ? '/ar' : ''), [lang]);

  const withPrefix = (path) => {
    if (path === '/' || path === '') return prefix || '/';
    return `${prefix}${path}`.replace(/\/+/g, '/');
  };

  const navLinks = [
    { name: t('nav.experience'), path: withPrefix('/experience') },
    { name: t('nav.skills'), path: withPrefix('/skills') },
    { name: t('nav.education'), path: withPrefix('/education') },
    { name: t('nav.services'), path: withPrefix('/services') },
    { name: t('nav.cv'), path: withPrefix('/cv') },
    { name: t('nav.contact'), path: withPrefix('/contact') },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      className="fixed top-0 inset-x-0 z-50 transition-all duration-300 py-3 bg-dark-950/80 backdrop-blur-lg border-b border-white/5 shadow-lg"
    >
      <div className="max-w-screen-lg mx-auto px-4 flex items-center justify-between">
        <Link 
          to={withPrefix('/')} 
          className="flex items-center gap-3 group rounded-lg focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 p-1 -m-1"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-primary-500 rounded-full blur-md opacity-50 group-hover:opacity-100 transition-opacity"></div>
            <img 
              src="/profile.png" 
              alt="Samir Djelal" 
              className="relative w-10 h-10 rounded-full border-2 border-white/10 object-cover"
            />
          </div>
          <span className="font-bold text-xl tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-white/70">
            Samir Djelal
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          <div className="flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative text-sm font-medium transition-colors hover:text-primary-400 rounded-lg px-2 -mx-2 focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 ${
                  isActive(link.path) ? 'text-primary-400' : 'text-white/70'
                }`}
              >
                {link.name}
                {isActive(link.path) && (
                  <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary-400 rounded-full" />
                )}
              </Link>
            ))}
          </div>
          <div className="h-4 w-px bg-white/10"></div>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Mobile Toggle */}
        <div className="md:hidden flex items-center gap-2">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 text-white/70 hover:text-white transition-colors focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 rounded-lg"
            aria-expanded={mobileMenuOpen}
            aria-label={mobileMenuOpen ? 'Close mobile menu' : 'Open mobile menu'}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden absolute top-full inset-x-0 bg-dark-900 border-b border-white/5 py-6 px-4 flex flex-col gap-4 animate-fade-in-down">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-lg font-medium rounded-lg px-2 -mx-2 focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 ${
                isActive(link.path) ? 'text-primary-400' : 'text-white/70'
              }`}
            >
              {link.name}
            </Link>
          ))}
          <div className="pt-4 border-t border-white/5 flex justify-between items-center">
            <span className="text-sm text-white/40">{t('nav.language')}</span>
            <LanguageSwitcher />
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
